import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Lock, Check, FileText, Pen, X, Clock, Sparkles } from "lucide-react";
import { toast } from "sonner";

const LOGO_URL = `/api/static/images/web/logo_mbs.png`;

const inputCls =
  "mt-2 bg-[#FBF9F5] border-[#BFBFBF]/70 h-11 text-[#1F3535] placeholder:text-[#6B6B6B]/45 rounded-xl focus-visible:ring-2 focus-visible:ring-[#D4AF37]/40 focus-visible:border-[#1F3535] transition-shadow font-body";
const labelCls = "text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] font-semibold nav-text";
const primaryBtn =
  "w-full bg-[#1F3535] text-white hover:bg-[#0F2424] disabled:opacity-40 transition-all h-12 rounded-full font-semibold tracking-[0.16em] uppercase text-[11px] shadow-md shadow-[#1F3535]/25 hover:shadow-lg hover:-translate-y-px disabled:hover:translate-y-0";

export default function FirstLoginFlow() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(user?.password_changed ? 2 : 1);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [contract, setContract] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [executionImmediate, setExecutionImmediate] = useState(null);
  const [consentContact, setConsentContact] = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(false);
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }
    setLoading(true);
    try {
      await api.post("/client/change-password", { new_password: newPassword });
      toast.success("Mot de passe modifié avec succès");
      await checkAuth();
      loadContract();
      setStep(2);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const loadContract = async () => {
    try {
      const { data } = await api.get("/client/contract");
      setContract(data.contract);
    } catch {
      toast.error("Erreur lors du chargement du contrat");
    }
  };

  useState(() => {
    if (step === 2) loadContract();
  });

  const initCanvas = useCallback((canvas) => {
    if (!canvas) return;
    canvasRef.current = canvas;
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = "#1F3535";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    isDrawingRef.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };
  const draw = (e) => {
    e.preventDefault();
    if (!isDrawingRef.current) return;
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };
  const endDraw = () => {
    isDrawingRef.current = false;
    setSignatureData(canvasRef.current?.toDataURL("image/png"));
  };
  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setSignatureData(null);
  };

  const handleSignContract = async () => {
    if (!accepted) {
      toast.error("Veuillez accepter les termes du contrat");
      return;
    }
    if (executionImmediate === null) {
      toast.error("Veuillez choisir si vous souhaitez une mise en service immédiate");
      return;
    }
    if (!consentContact) {
      toast.error("Veuillez accepter le traitement de vos données");
      return;
    }
    if (!signatureData) {
      toast.error("Veuillez signer le contrat");
      return;
    }
    setLoading(true);
    try {
      await api.post("/client/sign-contract", {
        signature_data: signatureData,
        execution_immediate: executionImmediate,
        consent_contact: consentContact,
        consent_marketing: consentMarketing,
      });
      toast.success("Contrat signé avec succès !");
      await checkAuth();
      navigate("/client?welcome=true");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F5] text-[#333333] px-4 py-10 pb-32 md:pb-12 relative overflow-hidden">
      {/* Ambient accents */}
      <div aria-hidden="true" className="absolute inset-0 bg-tech-grid opacity-50" />
      <div aria-hidden="true" className="absolute -top-40 -left-32 w-[480px] h-[480px] rounded-full bg-[radial-gradient(circle,rgba(196,106,74,0.07),transparent_60%)]" />
      <div aria-hidden="true" className="absolute -bottom-40 -right-32 w-[520px] h-[520px] rounded-full bg-[radial-gradient(circle,rgba(0,140,168,0.07),transparent_60%)]" />

      <div className="relative max-w-2xl mx-auto">
        {/* Brand header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2.5">
            <img src={LOGO_URL} alt="MonBeauSite" className="h-11 w-11 object-contain rounded-xl ring-1 ring-[#D4AF37]/25 shadow-sm bg-white" />
            <span className="text-[17px] font-semibold text-[#1F3535] tracking-tight font-body">
              MonBeauSite<span className="text-[#C46A4A]">.</span>com
            </span>
          </div>
          <p className="mt-3 text-[10px] uppercase tracking-[0.22em] text-[#C46A4A] font-semibold">
            Bienvenue · Activation de votre espace
          </p>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-4 mb-10">
          <div className={`flex items-center gap-2.5 ${step >= 1 ? "text-[#1F3535]" : "text-[#6B6B6B]/40"}`}>
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-mono-tech font-bold transition-colors ${
                step >= 1
                  ? "bg-[#1F3535] text-[#D4AF37] ring-2 ring-[#D4AF37]/30"
                  : "bg-[#BFBFBF]/40 text-[#6B6B6B]"
              }`}
            >
              {step > 1 ? <Check className="w-4 h-4 text-[#D4AF37]" strokeWidth={2.5} /> : "1"}
            </div>
            <span className="text-[11px] font-semibold tracking-[0.16em] uppercase nav-text hidden sm:inline">
              Mot de passe
            </span>
          </div>
          <div className={`h-px flex-1 transition-colors ${step >= 2 ? "bg-gradient-to-r from-[#1F3535] via-[#D4AF37] to-[#1F3535]" : "bg-[#BFBFBF]/60"}`} />
          <div className={`flex items-center gap-2.5 ${step >= 2 ? "text-[#1F3535]" : "text-[#6B6B6B]/40"}`}>
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-mono-tech font-bold transition-colors ${
                step >= 2 ? "bg-[#1F3535] text-[#D4AF37] ring-2 ring-[#D4AF37]/30" : "bg-[#BFBFBF]/40 text-[#6B6B6B]"
              }`}
            >
              2
            </div>
            <span className="text-[11px] font-semibold tracking-[0.16em] uppercase nav-text hidden sm:inline">
              Contrat
            </span>
          </div>
        </div>

        {step === 1 && (
          <div className="bg-white/95 backdrop-blur-sm border border-[#BFBFBF]/45 rounded-2xl p-8 shadow-sm shadow-[#1F3535]/[0.04] animate-fade-up">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-full bg-[#C46A4A]/10 border border-[#C46A4A]/20 flex items-center justify-center">
                <Lock className="w-5 h-5 text-[#C46A4A]" strokeWidth={1.6} />
              </div>
              <h2 className="text-[26px] font-medium text-[#1F3535] font-montserrat tracking-tight leading-snug">
                Personnalisez votre <span className="font-playfair italic text-[#C46A4A] font-normal">mot de passe.</span>
              </h2>
            </div>
            <p className="text-[#555555] mb-7 text-sm font-body font-light leading-relaxed">
              Pour la sécurité de votre compte, veuillez définir un nouveau mot de passe personnel.
            </p>
            <form onSubmit={handlePasswordChange} className="space-y-5">
              <div>
                <Label className={labelCls}>Nouveau mot de passe</Label>
                <Input
                  data-testid="new-password-input"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  placeholder="Minimum 8 caractères"
                  className={inputCls}
                />
              </div>
              <div>
                <Label className={labelCls}>Confirmer le mot de passe</Label>
                <Input
                  data-testid="confirm-password-input"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                  placeholder="Confirmez votre mot de passe"
                  className={inputCls}
                />
              </div>
              {error && (
                <p data-testid="password-error" className="text-red-700 text-xs bg-red-50 border border-red-200 px-4 py-2.5 rounded-xl font-body">
                  {error}
                </p>
              )}
              <Button type="submit" data-testid="change-password-submit" disabled={loading} className={primaryBtn}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Valider le mot de passe"}
              </Button>
            </form>
          </div>
        )}

        {step === 2 && (
          <div className="bg-white/95 backdrop-blur-sm border border-[#BFBFBF]/45 rounded-2xl p-8 shadow-sm shadow-[#1F3535]/[0.04] animate-fade-up">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-full bg-[#C46A4A]/10 border border-[#C46A4A]/20 flex items-center justify-center">
                <FileText className="w-5 h-5 text-[#C46A4A]" strokeWidth={1.6} />
              </div>
              <h2 className="text-[26px] font-medium text-[#1F3535] font-montserrat tracking-tight leading-snug">
                Votre <span className="font-playfair italic text-[#C46A4A] font-normal">contrat.</span>
              </h2>
            </div>
            <p className="text-[#555555] mb-5 text-sm font-body font-light leading-relaxed">
              Veuillez lire attentivement votre contrat puis le signer électroniquement.
            </p>

            <div
              data-testid="contract-content"
              className="bg-[#FBF9F5] border border-[#BFBFBF]/55 rounded-xl p-6 max-h-96 overflow-y-auto mb-6 text-[#444] text-[13px] leading-relaxed whitespace-pre-wrap font-mono-tech"
            >
              {contract || "Chargement du contrat…"}
            </div>

            {/* Informations précontractuelles */}
            <div className="bg-[#1F3535]/[0.04] border border-[#1F3535]/15 rounded-2xl p-5 mb-6" data-testid="precontractual-info">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-[#1F3535]" strokeWidth={1.7} />
                <p className="text-[10px] font-semibold text-[#1F3535] uppercase tracking-[0.18em] nav-text">
                  Informations précontractuelles — Délais de livraison
                </p>
              </div>
              <p className="text-sm text-[#444] leading-relaxed font-body">
                Votre site Internet sera livré au maximum dans un délai de{" "}
                <strong className="text-[#1F3535] font-mono-tech">10 jours ouvrés</strong> à compter de :
              </p>
              <ul className="mt-2.5 space-y-1.5 text-sm text-[#444] font-body">
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-[#C46A4A] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span>
                    La <strong className="text-[#1F3535]">date de signature</strong> du contrat, si vous acceptez l'option d'exécution immédiate (renonciation au délai de rétractation).
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-[#C46A4A] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span>
                    L'expiration du <strong className="text-[#1F3535]">délai légal de rétractation de 14 jours</strong>, si vous ne renoncez pas à ce droit.
                  </span>
                </li>
              </ul>
            </div>

            <div className="flex items-start gap-3 mb-6">
              <Checkbox
                id="accept"
                data-testid="accept-contract-checkbox"
                checked={accepted}
                onCheckedChange={(checked) => setAccepted(checked === true)}
                className="border-[#BFBFBF] data-[state=checked]:bg-[#1F3535] data-[state=checked]:border-[#1F3535] mt-0.5"
              />
              <Label htmlFor="accept" className="text-[#555555] text-sm cursor-pointer leading-relaxed font-body">
                J'ai lu et j'accepte les termes du contrat de prestation de services web MonBeauSite.
              </Label>
            </div>

            {/* Execution immediate option */}
            {accepted && (
              <div className="mb-6 animate-fade-up" data-testid="execution-immediate-section">
                <div className="bg-gradient-to-br from-[#C46A4A]/[0.06] to-[#D4AF37]/[0.06] border border-[#C46A4A]/20 rounded-2xl p-5">
                  <p className="text-sm text-[#1F3535] font-medium leading-relaxed mb-4 font-playfair italic">
                    Pour que je puisse lancer la création de votre site dès demain et que vous soyez en ligne la semaine prochaine, j'ai juste besoin que vous acceptiez la demande de mise en service immédiate.
                  </p>
                  <div className="bg-white border border-[#BFBFBF]/60 rounded-xl p-4 mb-4">
                    <p className="text-[10px] font-semibold text-[#C46A4A] uppercase tracking-[0.18em] mb-2 nav-text">
                      Option d'exécution immédiate
                    </p>
                    <p className="text-[12px] text-[#555555] leading-relaxed font-body">
                      Je demande expressément l'exécution immédiate de la prestation de services avant la fin du délai légal de rétractation de 14 jours. Je reconnais être informé qu'en demandant le début de l'exécution des travaux avant la fin de ce délai, je renonce expressément à mon droit de rétractation une fois que le contrat aura été pleinement exécuté par MonBeauSite.
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      data-testid="execution-immediate-accept"
                      onClick={() => setExecutionImmediate(true)}
                      className={`flex-1 h-11 rounded-full text-[11px] font-semibold tracking-[0.16em] uppercase transition-all ${
                        executionImmediate === true
                          ? "bg-[#1F3535] text-white shadow-md shadow-[#1F3535]/25 ring-2 ring-[#D4AF37]/30"
                          : "bg-white border border-[#BFBFBF] text-[#1F3535] hover:bg-[#1F3535]/5 hover:border-[#1F3535]/40"
                      }`}
                    >
                      {executionImmediate === true && <Check className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.5} />}
                      Accepter
                    </Button>
                    <Button
                      type="button"
                      data-testid="execution-immediate-decline"
                      onClick={() => setExecutionImmediate(false)}
                      className={`flex-1 h-11 rounded-full text-[11px] font-semibold tracking-[0.16em] uppercase transition-all ${
                        executionImmediate === false
                          ? "bg-[#6B6B6B] text-white shadow-md"
                          : "bg-white border border-[#BFBFBF] text-[#6B6B6B] hover:bg-[#6B6B6B]/5"
                      }`}
                    >
                      {executionImmediate === false && <X className="w-3.5 h-3.5 mr-1.5" />}
                      Décliner
                    </Button>
                  </div>
                  {executionImmediate === false && (
                    <p className="text-xs text-[#6B6B6B] italic mt-3 font-body">
                      Pas de souci ! Votre délai de rétractation de 14 jours sera respecté. La création de votre site débutera à l'issue de ce délai.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Consentements RGPD */}
            {accepted && executionImmediate !== null && (
              <div className="mb-6 space-y-3 animate-fade-up" data-testid="consent-section">
                <p className="text-[10px] font-semibold text-[#C46A4A] uppercase tracking-[0.18em] mb-1 nav-text">
                  Consentements
                </p>
                <label className="flex items-start gap-2.5 cursor-pointer group">
                  <Checkbox
                    data-testid="consent-contact-checkbox"
                    checked={consentContact}
                    onCheckedChange={(checked) => setConsentContact(checked === true)}
                    className="border-[#BFBFBF] data-[state=checked]:bg-[#1F3535] data-[state=checked]:border-[#1F3535] mt-0.5"
                  />
                  <span className="text-[12px] text-[#555555] leading-relaxed group-hover:text-[#1F3535] transition-colors font-body">
                    J'accepte que mes données soient utilisées dans le cadre de ma relation contractuelle avec MonBeauSite. *
                  </span>
                </label>
                <label className="flex items-start gap-2.5 cursor-pointer group">
                  <Checkbox
                    data-testid="consent-marketing-checkbox"
                    checked={consentMarketing}
                    onCheckedChange={(checked) => setConsentMarketing(checked === true)}
                    className="border-[#BFBFBF] data-[state=checked]:bg-[#1F3535] data-[state=checked]:border-[#1F3535] mt-0.5"
                  />
                  <span className="text-[12px] text-[#555555] leading-relaxed group-hover:text-[#1F3535] transition-colors font-body">
                    J'accepte de recevoir les conseils d'expert d'Emilien pour optimiser ma présence en ligne et faire grandir mon activité (facultatif).
                  </span>
                </label>
              </div>
            )}

            {/* Signature */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <Label className={`${labelCls} flex items-center gap-1.5`}>
                  <Pen className="w-3 h-3 text-[#D4AF37]" strokeWidth={2} /> Signature électronique
                </Label>
                <button
                  data-testid="clear-signature-btn"
                  onClick={clearSignature}
                  className="text-[#6B6B6B]/70 hover:text-[#C46A4A] text-[11px] underline underline-offset-2 transition-colors font-body"
                >
                  Effacer
                </button>
              </div>
              <canvas
                ref={initCanvas}
                data-testid="signature-canvas"
                width={600}
                height={150}
                className="signature-canvas w-full bg-[#FBF9F5] border border-[#BFBFBF]/60 rounded-xl cursor-crosshair hover:border-[#D4AF37]/45 transition-colors"
                onMouseDown={startDraw}
                onMouseMove={draw}
                onMouseUp={endDraw}
                onMouseLeave={endDraw}
                onTouchStart={startDraw}
                onTouchMove={draw}
                onTouchEnd={endDraw}
              />
              {signatureData && (
                <p className="text-[#1F3535] text-[11px] mt-2 flex items-center gap-1.5 font-body">
                  <Check className="w-3.5 h-3.5 text-[#C46A4A]" strokeWidth={2.5} /> Signature enregistrée
                </p>
              )}
            </div>

            <Button
              data-testid="sign-contract-submit"
              onClick={handleSignContract}
              disabled={loading || !accepted || executionImmediate === null || !consentContact || !signatureData}
              className={primaryBtn}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Sparkles className="w-3.5 h-3.5 mr-2 text-[#D4AF37]" strokeWidth={2} /> Signer le contrat
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
